'use client';

import { Button } from '@/app/components/ui/Button';
import Modal from '@/app/components/ui/Modal';
import type { EntitlementGateResult } from '@/types/entitlement';
import { LockKeyhole, X } from 'lucide-react';
import { useTranslations } from 'next-intl';

type AccessRestrictedModalProps = {
  open: boolean;
  gateResult?: EntitlementGateResult | null;
  onOpenChange: (open: boolean) => void;
  zIndex?: number;
};

export default function AccessRestrictedModal({
  open,
  gateResult,
  onOpenChange,
  zIndex,
}: AccessRestrictedModalProps) {
  const tPlans = useTranslations('settings.plansPage');
  const blockedGateResult = gateResult && !gateResult.allowed ? gateResult : null;
  const isAdminRequired = blockedGateResult?.accessRestriction === 'admin_required';

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      zIndex={zIndex}
      bodyClassName="flex w-[calc(100vw-32px)] max-w-[420px] flex-col gap-0 overflow-hidden rounded-[4px] border border-border bg-background p-0 shadow-[0px_16px_32px_-12px_rgba(14,18,27,0.1)] sm:max-w-[420px]"
    >
      <div className="relative flex shrink-0 flex-col items-center gap-2 px-5 py-8 text-center">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 h-6 w-6 rounded-[4px] p-0 text-muted-foreground"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </Button>

        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#E0FAEC]">
          <LockKeyhole className="h-6 w-6 text-[#06B560]" />
        </div>

        <h2 className="max-w-[360px] text-center text-[24px] font-medium leading-8 tracking-[-0.36px] text-foreground">
          {isAdminRequired
            ? tPlans('entitlementGate.adminAccessRequiredTitle')
            : tPlans('entitlementGate.viewOnlyAccessTitle')}
        </h2>

        <p className="mt-1 max-w-[360px] text-center text-[16px] leading-6 tracking-[-0.176px] text-muted-foreground">
          {isAdminRequired
            ? tPlans('entitlementGate.adminAccessRequiredDescription')
            : tPlans('entitlementGate.viewOnlyAccessDescription')}
        </p>
      </div>
    </Modal>
  );
}
